import { CanvasRenderingTarget2D } from 'fancy-canvas';
import {
  AutoscaleInfo,
  Coordinate,
  IChartApi,
  ISeriesApi,
  ISeriesPrimitive,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  SeriesOptionsMap,
  SeriesType,
  Time
} from 'lightweight-charts';

interface ViewPoint {
  x: Coordinate | null;
  y: Coordinate | null;
}

export interface MarkerOptions {
  color: string;
  size: number;
  offset: number;
  getBarSpacing: () => number;
}

const defaultMarkerOptions: MarkerOptions = {
  color: 'rgb(0, 100, 255)',
  size: 8,
  offset: 0,
  getBarSpacing: () => 6
};

class MarkerPaneRenderer implements IPrimitivePaneRenderer {
  _point: ViewPoint;
  _options: MarkerOptions;

  constructor(point: ViewPoint, options: MarkerOptions) {
    this._point = point;
    this._options = options;
  }

  draw(target: CanvasRenderingTarget2D) {
    target.useBitmapCoordinateSpace((scope: any) => {
      if (this._point.x === null || this._point.y === null) return;

      const ctx = scope.context;
      const x = Math.round(this._point.x * scope.horizontalPixelRatio);
      const y = Math.round(this._point.y * scope.verticalPixelRatio);

      // Calculate scale factor based on current bar spacing
      const currentBarSpacing = this._options.getBarSpacing();
      const baseBarWidth = 2; // Reference bar width at default zoom

      // Adjusted scaling factor with division by 10 to prevent excessive growth
      let scaleFactor = currentBarSpacing / baseBarWidth / 10;

      if (scaleFactor > 0.9) {
        scaleFactor = 0.9;
      }

      // Apply scaling to size and offset
      const size =
        this._options.size *
        scaleFactor *
        Math.min(scope.horizontalPixelRatio, scope.verticalPixelRatio);
      const offset =
        this._options.offset * scaleFactor * scope.horizontalPixelRatio;

      ctx.fillStyle = this._options.color;

      // Draw right-pointing arrow
      ctx.beginPath();
      ctx.moveTo(x - offset - size, y - size);
      ctx.lineTo(x - offset, y);
      ctx.lineTo(x - offset - size, y + size);
      ctx.closePath();
      ctx.fill();
    });
  }
}

class MarkerPaneView implements IPrimitivePaneView {
  _source: Marker;
  _point: ViewPoint = { x: null, y: null };

  constructor(source: Marker) {
    this._source = source;
  }

  update() {
    const series = this._source._series;
    const timeScale = this._source._chart.timeScale();

    // Convert time/price to coordinates
    const y = series.priceToCoordinate(this._source._price);
    const x = timeScale.timeToCoordinate(this._source._time);

    this._point = {
      x: x !== null ? (x as Coordinate) : null,
      y: y
    };
  }

  renderer() {
    return new MarkerPaneRenderer(this._point, this._source._options);
  }
}

export class Marker implements ISeriesPrimitive<Time> {
  _chart: IChartApi;
  _series: ISeriesApi<keyof SeriesOptionsMap>;
  _time: Time;
  _price: number;
  _paneViews: MarkerPaneView[];
  _options: MarkerOptions;

  constructor(
    chart: IChartApi,
    series: ISeriesApi<SeriesType>,
    time: Time,
    price: number,
    options?: Partial<MarkerOptions>
  ) {
    this._chart = chart;
    this._series = series;
    this._time = time;
    this._price = price;
    this._options = {
      ...defaultMarkerOptions,
      ...options,
      getBarSpacing: () => {
        const timeScale = chart.timeScale();
        return 'barSpacing' in timeScale
          ? (timeScale as any).barSpacing
          : timeScale.options().barSpacing;
      }
    };
    this._paneViews = [new MarkerPaneView(this)];
  }

  autoscaleInfo(): AutoscaleInfo | null {
    return null;
  }

  updateAllViews() {
    this._paneViews.forEach((pw) => pw.update());
  }

  paneViews() {
    return this._paneViews;
  }
}

// Helper function to create markers
export function createMarker(
  chart: IChartApi,
  series: ISeriesApi<SeriesType>,
  time: Time,
  price: number,
  color: string = 'rgb(0, 100, 255)',
  size: number = 5,
  offset: number = 0
): Marker {
  return new Marker(chart, series, time, price, {
    color,
    size,
    offset,
    getBarSpacing: () => {
      const timeScale = chart.timeScale();
      return 'barSpacing' in timeScale
        ? (timeScale as any).barSpacing
        : timeScale.options().barSpacing;
    }
  });
}
